import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CellClickedEvent,
  ColDef,
  GridApi,
  SideBarDef,
} from "ag-grid-community";
import { useNavigate } from "react-router";

import { AgGridReact } from "ag-grid-react";
import { Action, CTADropdown } from "../../components/shared/Dropdown";
import { agConfigDashboardSideBar } from "../dashboardPage/AgGridConfig";

import {
  BeneficiaryModel,
  ShareholderDataModel,
} from "../../types/Shareholder";

import { Box, HStack, useGetCompanyName } from "../../components/utils";
import GenericTableHeader from "../../shared/TableHeader";
import { IconCTAButton } from "../quickRound/CTAButtonComponents";
import QuickSearch from "../rounds/QuickSearch";
import { useAuthStore } from "../../store";
import {
  useGetOnFilterState,
  usePostOnFilterState,
} from "../dashboardPage/AgGridCacheQuery";
import { tableCellStyle } from "../../components/TableComponent";
import { ExportImport } from "../Utility/GrantsTable";
import {
  exportSharecertificateReport,
  exportShareholderReport,
} from "../../api/Report";
import { downloadExcel } from "../../utils/DownloadFile";
import {
  BeneficiaryNameRender,
  HoldingPercentageRender,
  NumberOfSharesRender,
  SecurityClassRender,
  EmailRender,
  AddressRender,
  DateRender,
} from "./BeneficiariesAgComponent";
import { useAgGridTableState } from "../../store/agGridTableStore";
import SearchInput from "../../components/shared/SearchInput";
import { handleEventForTracking } from "../../amplitudeAnalytics";
import { BeneficiaryDocumentModal } from "../../modals/BeneficiaryDocumentModal";

function AgGridBeneficiariesTable({
  isPreviewOnly = false,
  beneficiaries,
  currencySymbol,
  currencyType,
}: {
  isPreviewOnly?: boolean;
  beneficiaries: BeneficiaryModel[];
  currencySymbol: string;
  currencyType: string;
}) {
  const companyName = useGetCompanyName();
  const companyId = useAuthStore().companyId ?? "";
  const userId = useAuthStore().user?.userId ?? 0;
  const gridApi = useRef<any>(null);
  const { refetch, data: filterState } = useGetOnFilterState(
    `${userId}`,
    companyId
  );
  const agTableStore = useAgGridTableState();

  useEffect(() => {
    refetch().then((data) => {
      if (data.data) agTableStore.setState(data.data);
      setColumnSetting(data.data?.beneficiaryColumnModel);
      setFilterSetting(data.data?.beneficiaryFilterModel);
    });
  }, []);

  const { mutate: postOnFilter } = usePostOnFilterState();

  function getColumnSetting() {
    if (gridApi.current) return gridApi.current.columnApi?.getColumnState();
    return {};
  }

  function setColumnSetting(model: any) {
    if (gridApi.current)
      gridApi.current.columnApi.applyColumnState({ state: model });
    return {};
  }

  // Gets filter model via the grid API
  const getFilterSetting = () => {
    if (gridApi.current) return gridApi.current.api?.getFilterModel();
    return {};
  };

  const setFilterSetting = (model: any) => {
    if (gridApi.current) return gridApi.current.api?.setFilterModel(model);
    return {};
  };

  const uploadFilterAndColumn = async () => {
    const columnState = await getColumnSetting();
    const filterState = await getFilterSetting();
    postOnFilter({
      userId: `${userId}`,
      companyId,
      filterData: {
        ...agTableStore.state,
        beneficiaryColumnModel: columnState,
        beneficiaryFilterModel: filterState,
      },
    });
  };

  const cin = "";

  const componentsRegistery = useMemo(
    () => ({
      beneficiaryName: memo(BeneficiaryNameRender),
      securityClass: memo(SecurityClassRender),
      numberOfShares: memo(NumberOfSharesRender),
      holdingPercentage: memo(HoldingPercentageRender),
      email: memo(EmailRender),
      address: memo(AddressRender),
      date: memo(DateRender),
    }),
    []
  );

  const navigate = useNavigate();

  const onClickShareholderName = (id: string) => {
    handleEventForTracking({ eventName: "Shareholder Name" });
    navigate(`/shareholders/individual/${id}`);
  };

  const onClickBeneficiaryName = (bInvestorId: string) => {
    handleEventForTracking({ eventName: "Beneficiary Name" });
    navigate(`/beneficiaries/individual/${bInvestorId}`);
  };

  const isValidDate = (date: string) =>
    Number.isNaN(date) &&
    new Date(date).toString() !== "Invalid Date" &&
    !Number.isNaN(Date.parse(date));

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  );

  const handleBeneficiaryAction = (action: Action, rowData: any) => {
    if (action.name === "Documents") {
      // Find the original beneficiary object from the beneficiaries array
      const beneficiary = beneficiaries.find(
        (b) =>
          b.name === rowData.beneficiaryName &&
          b.certificateId === rowData.certificateId
      );

      if (beneficiary) {
        setDocumentModal({
          isOpen: true,
          beneficiary,
          investorId: beneficiary.bInvestorId,
        });
      }
    }
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Beneficiary",
        field: "beneficiaryName",
        cellRenderer: "beneficiaryName",
        cellRendererParams: {
          onClickBeneficiaryName,
        },
        initialWidth: 250,
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Security Class",
        field: "securityClass",
        filter: "agSetColumnFilter",
        cellRenderer: "securityType",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "No. of Securities",
        field: "numberOfShares",
        filter: "agNumberColumnFilter",
        cellRenderer: "numberOfShares",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipNumberOfShares",
      },
      {
        headerName: "Holding (%)",
        field: "holdingPercentage",
        filter: "agNumberColumnFilter",
        cellRenderer: "holdingPercentage",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipHoldingPercentage",
      },
      {
        headerName: "Email",
        field: "email",
        filter: "agTextColumnFilter",
        cellRenderer: "email",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        initialWidth: 200,
      },
      {
        headerName: "Address",
        field: "address",
        filter: "agTextColumnFilter",
        cellRenderer: "address",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        initialWidth: 250,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "Allotment Date",
        field: "date",
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (dateFromFilter: Date, cellValue: any) => {
            if (cellValue == null) {
              return 0;
            }
            const cellDate = new Date(cellValue);
            if (cellDate < dateFromFilter) {
              return -1;
            } else if (cellDate > dateFromFilter) {
              return 1;
            }
            return 0;
          },
        },
        cellRenderer: "date",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        initialWidth: 150,
      },
      {
        headerName: "",
        field: "actions",
        pinned: "right",
        hide: false,
        width: 80,
        maxWidth: 80,
        filter: false,
        menuTabs: [],
        colId: "action-column",
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        cellStyle: { "padding-top": "12px", "line-height": "18px" },
        resizable: false,
        sortable: false,
        cellRendererParams: ({ value }: { value: any }) => value?.props,
        cellRenderer: (params: any) => (
          <CTADropdown
            actions={[
              {
                name: "Documents",
                disabled: false,
              },
            ]}
            onAction={(action) => {
              handleBeneficiaryAction(action, {
                beneficiaryName: params.data.beneficiaryName,
                certificateId: params.data.certificateId,
              });
            }}
          />
        ),
      },
    ],
    [isPreviewOnly, handleBeneficiaryAction, onClickBeneficiaryName]
  );

  const gridOptions = {
    suppressRowVirtualisation: true, // Add this line to disable row virtualization
    paginationAutoPageSize: false, // Optional: Set to false if using custom pagination
    suppressScrollOnNewData: true, // Optional: Set to true to prevent scrolling to the top on
  };
  const rowData = useMemo(
    () =>
      beneficiaries?.map((template) => ({
        beneficiaryName: template.name,
        id: template.id,
        bInvestorId: template.bInvestorId,
        tooltipBeneficiaryName: template.name,
        securityClass: template.securityClass,
        numberOfShares: template.numberOfShares,
        tooltipNumberOfShares:
          template.numberOfShares.toLocaleString(currencyType),
        holdingPercentage: template.holdingPercentage * 100,
        tooltipHoldingPercentage:
          template.holdingPercentage.toLocaleString(currencyType),
        email: template.email,
        address: template.address,
        date: template.date,
        currencySymbol,
        currencyType,
        certificateId: template.certificateId,
      })),
    [beneficiaries, currencySymbol, currencyType]
  );

  const onPageSizeChanged = useCallback(() => {
    const value = (document.getElementById("page-size") as HTMLInputElement)
      .value;
    gridApi.current?.paginationSetPageSize(Number(value));
  }, []);

  const handleCellClick = (cellParams: CellClickedEvent<any, any>) => {
    if (cellParams.column.getColId() !== "action-column") {
      const template = cellParams.data;
      if (cellParams.column.getColId() === "shareholderName") {
        onClickShareholderName(template.id);
      }
    }
  };
  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [documentModal, setDocumentModal] = useState<{
    isOpen: boolean;
    beneficiary: BeneficiaryModel | null;
    investorId: string | null;
  }>({
    isOpen: false,
    beneficiary: null,
    investorId: null,
  });

  const openToolPanel = (key: any) => {
    if (key === "columns") {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      }
    } else if (key === "filters") {
      if (gridApi) {
        if (!isFilterOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };

  const [filteredRowData, setFilteredRowData] = useState<any>([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const onAgGridFilterChanged = (grid: any) => {
    const filtersApplied = grid.api.isAnyFilterPresent();
    setIsFilterApplied(filtersApplied);

    const filteredData = grid.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);
    setFilteredRowData(filteredData);
    uploadFilterAndColumn();
  };

  return (
    <>
      <HStack className="justify-between py-2 items-center  bg-white rounded-md">
        <GenericTableHeader
          heading={"Beneficiaries"}
          subHeading={"Beneficiaries"}
          count={
            isFilterApplied === true ? filteredRowData.length : rowData.length
          }
        />
        <HStack className="justify-end py-2 items-center gap-4 mr-4">
          {/* <QuickSearch
            placeholder={`Search`}
            onChange={(e) => {
              gridApi.current.api.setQuickFilter(e.target.value);
            }}
          /> */}
          <SearchInput
            placeholder={`Search`}
            onChange={(e: any) => {
              gridApi.current.api.setQuickFilter(e.target.value);
            }}
          />
          <IconCTAButton
            value={"Columns"}
            onClick={() => openToolPanel("columns")}
            iconName={"fluent:column-triple-edit-20-regular"}
            className={`px-4 font-medium items-center flex flex-row ${
              isColumnOpen ? "text-orange-501" : "text-gray-400"
            }`}
          />
          <IconCTAButton
            value={"Filters"}
            onClick={() => openToolPanel("filters")}
            iconName={"material-symbols:filter-alt"}
            className={`px-4 font-medium items-center flex flex-row ${
              isFilterOpen ? "text-orange-501" : "text-gray-400"
            }`}
          />
        </HStack>
      </HStack>
      <HStack className="justify-between w-full">
        <Box
          style={{
            height: `${
              (rowData.length >= 10 ? 10 : rowData.length + 3) * 58
            }px`,
          }}
          className="w-full max-h-full overflow-x-auto ag-theme-material h-full "
        >
          <AgGridReact
            sideBar={agConfigDashboardSideBar}
            onGridReady={(params) => {
              // Store the grid API referen
              gridApi.current = params;
            }}
            components={componentsRegistery}
            alwaysShowHorizontalScroll
            alwaysMultiSort
            animateRows={true}
            defaultColDef={defaultColDef}
            onFilterChanged={onAgGridFilterChanged}
            rowData={rowData}
            // onCellClicked={handleCellClick}
            columnDefs={columnDefs}
            pagination={false}
            suppressRowTransform={true}
            suppressCopyRowsToClipboard={true}
            suppressCopySingleCellRanges={true}
            suppressCellFocus={true}
            suppressMenuHide={false}
            // noRowsOverlayComponent={showNoRowsOverlay}
            tooltipShowDelay={1000}
            tooltipInteraction={true}
            rowClass={"border-t border-dashed"}
            gridOptions={gridOptions}
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
            }
            getRowStyle={(params) => {
              if (params.rowIndex % 2 === 0) {
                return { background: "#f8f8f8" };
              } else {
                return { background: "#ffffff" };
              }
            }}
          ></AgGridReact>
        </Box>
      </HStack>

      {/* Document Management Modal */}
      {documentModal.beneficiary && (
        <BeneficiaryDocumentModal
          investorId={documentModal.investorId!}
          isOpen={documentModal.isOpen}
          onClose={() =>
            setDocumentModal({
              isOpen: false,
              beneficiary: null,
              investorId: null,
            })
          }
          beneficiary={documentModal.beneficiary}
        />
      )}
    </>
  );
}

export default AgGridBeneficiariesTable;
